/**
 * F-126 "add music" dialog — the sound designer's one input: a LINK or an
 * UPLOAD, for one mood run's slot. Since F-139 the slot is a LIST: the dialog
 * shows the stems already there (each with its own preview and Remove) and
 * every save ADDS one, because a mood is reviewed between at least
 * `BGM_MIN_STEMS` candidates and a replace would keep the slot at one forever.
 *
 * Both paths end in the same doc write (`addCellBgmStem`), which is why they
 * share a dialog rather than living behind two buttons: the choice is about
 * where the file is, not about what the designer is doing.
 *
 * Upload writes the cell only AFTER the bytes land. A stem row pointing at an
 * assetId whose upload failed is a play button that 404s for the whole team,
 * and unlike a bad link nobody can see what it was meant to be.
 */
import { BGM_MIN_STEMS, bgmStemsOf, type BgmStem } from "@kola/shared";
import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { uploadAudioAsset } from "../../core/api.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { openExternalUrl } from "../../core/host.js";
import { showToast } from "../../ui/toast.js";
import {
  isDirectAudioUrl,
  isSafeHttpUrl,
  stemLinkUrl,
  stemPlaybackUrl,
  titleFromFileName,
  titleFromUrl,
  trimTitle,
} from "./stem.js";
import { bgmUi } from "./store.js";

export interface BgmStemDialogProps {
  session: BoardSession;
}

export function BgmStemDialog({ session }: BgmStemDialogProps) {
  const editing = useStore(bgmUi, (s) => s.editing);
  if (editing === null) return null;
  // Keyed on the slot so switching runs resets the form instead of carrying
  // one run's half-typed link into the next.
  return (
    <BgmStemDialogBody
      key={`${editing.sheetId}:${editing.rowId}:${editing.colId}`}
      session={session}
    />
  );
}

function BgmStemDialogBody({ session }: BgmStemDialogProps) {
  const editing = useStore(bgmUi, (s) => s.editing);
  // Re-read the slot after every write of our own (`refresh`), so the list
  // shows what just landed without subscribing the dialog to the whole doc.
  const [, bump] = useState(0);
  const refresh = () => bump((n) => n + 1);
  const existing =
    editing === null
      ? undefined
      : session.board.getCellBgm(editing.sheetId, editing.rowId, editing.colId);
  const stems = existing === undefined ? [] : bgmStemsOf(existing);

  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  // True once the user edits the title by hand — after that, pasting a
  // different link must not overwrite what they chose to call it.
  const titleTouched = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const alive = useRef(true);
  useEffect(() => () => void (alive.current = false), []);

  if (editing === null) return null;
  const close = () => bgmUi.getState().closeEditor();
  const runName = editing.label || "this mood";

  // A link Kola will accept but cannot hand to an <audio> element.
  const trimmedUrl = url.trim();
  const linkIsPage = isSafeHttpUrl(trimmedUrl) && !isDirectAudioUrl(trimmedUrl);

  /** ADD a stem to the slot — never replace (module note). */
  const add = (stem: BgmStem) => {
    let wrote = false;
    session.batch(() => {
      session.board.addCellBgmStem(editing.sheetId, editing.rowId, editing.colId, stem);
      wrote = true;
    });
    // F-45: while frozen, session.batch never runs its callback — say so
    // rather than clearing the form as if the stem had landed.
    if (!wrote) {
      showToast("Offline — the music wasn't saved");
      return;
    }
    const count = stems.length + 1;
    showToast(
      `"${stem.title}" added for ${runName} — ${count} of ${BGM_MIN_STEMS} stems${
        count >= BGM_MIN_STEMS ? " ✓" : ""
      }`,
    );
    setUrl("");
    setTitle("");
    titleTouched.current = false;
    refresh();
  };

  const saveLink = () => {
    const link = url.trim();
    if (!isSafeHttpUrl(link)) {
      setError("Paste a full http:// or https:// link to the track.");
      return;
    }
    add({ source: "url", url: link, title: trimTitle(title) || titleFromUrl(link) });
  };

  const upload = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const result = await uploadAudioAsset(session.boardId, file);
      if (!alive.current) return;
      add({
        source: "asset",
        assetId: result.assetId,
        title: trimTitle(title) || titleFromFileName(file.name),
      });
    } catch (err) {
      if (!alive.current) return;
      setError(err instanceof Error ? err.message : "Upload failed. Try again.");
    } finally {
      if (alive.current) setUploading(false);
    }
  };

  const remove = (index: number) => {
    let wrote = false;
    session.batch(() => {
      // Removing the last stem empties the cell — the slot stays, the music
      // goes, and the coverage counts the run as uncovered again.
      session.board.removeCellBgmStem(editing.sheetId, editing.rowId, editing.colId, index);
      wrote = true;
    });
    if (!wrote) {
      showToast("Offline — nothing was changed");
      return;
    }
    if (playing === index) setPlaying(null);
    refresh();
  };

  const missing = Math.max(0, BGM_MIN_STEMS - stems.length);
  const playingUrl =
    playing === null || stems[playing] === undefined
      ? null
      : stemPlaybackUrl(stems[playing] as BgmStem);

  return (
    <div className="bgm-dialog" role="dialog" aria-label="Music for this mood">
      <div className="bgm-dialog-head">
        <span className="bgm-dialog-title">Music for {runName}</span>
        <span className="spacer" />
        <CloseButton onClick={close} />
      </div>

      {stems.length > 0 && (
        <ul className="bgm-stem-list" aria-label="Stems in this slot">
          {stems.map((stem, index) => {
            const playable = stemPlaybackUrl(stem) !== null;
            const link = stemLinkUrl(stem);
            return (
              <li key={`${index}:${stem.title}`} className="bgm-stem">
                {/* A page link (YouTube, Spotify) cannot be previewed here —
                    offer to OPEN it instead, as the chip does (F-126). */}
                {!playable && link !== null ? (
                  <button
                    type="button"
                    className="bgm-slot-play bgm-slot-open"
                    aria-label={`Open ${stem.title} in your browser`}
                    onClick={() => openExternalUrl(link)}
                  >
                    ↗
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bgm-slot-play"
                    disabled={!playable}
                    aria-label={playing === index ? "Stop preview" : `Preview ${stem.title}`}
                    onClick={() => setPlaying((current) => (current === index ? null : index))}
                  >
                    {playing === index ? "■" : "▶"}
                  </button>
                )}
                <span className="bgm-stem-title" title={stem.title}>
                  {stem.title}
                </span>
                <span className="bgm-stem-source">{stem.source === "url" ? "link" : "file"}</span>
                <button
                  type="button"
                  className="bgm-btn bgm-btn-quiet"
                  onClick={() => remove(index)}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {playingUrl !== null && (
        <audio src={playingUrl} autoPlay onEnded={() => setPlaying(null)} onError={() => setPlaying(null)} />
      )}

      <div className={`bgm-stem-status${missing === 0 ? " bgm-stem-status-ready" : ""}`}>
        {missing === 0
          ? `${stems.length} stems — ready to review.`
          : `${stems.length} of ${BGM_MIN_STEMS} stems. Add ${missing} more — a mood is reviewed between at least ${BGM_MIN_STEMS} candidates.`}
      </div>

      <label className="bgm-field">
        <span className="bgm-field-label">Link</span>
        <input
          type="url"
          className="bgm-input"
          placeholder="https://…"
          value={url}
          autoFocus
          onChange={(event) => {
            setUrl(event.target.value);
            setError(null);
            if (!titleTouched.current) setTitle(titleFromUrl(event.target.value));
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") saveLink();
            if (event.key === "Escape") close();
          }}
        />
      </label>

      <label className="bgm-field">
        <span className="bgm-field-label">Title</span>
        <input
          type="text"
          className="bgm-input"
          placeholder="What to call it on the board"
          value={title}
          onChange={(event) => {
            titleTouched.current = true;
            setTitle(event.target.value);
          }}
        />
      </label>

      {/* Say it BEFORE the link is saved, not after: a YouTube or Spotify
          link is a perfectly good stem reference and the dialog takes it —
          but the slot will offer to OPEN it rather than to play it, and a
          designer who expected a preview button should learn that here. */}
      {linkIsPage && (
        <div className="bgm-dialog-note">
          Kola can&rsquo;t preview this link — the slot will open it in your browser.
          Paste a direct .mp3/.wav link, or upload the file, to get a preview.
        </div>
      )}

      {error !== null && <div className="bgm-dialog-error">{error}</div>}

      <div className="bgm-dialog-actions">
        <button
          type="button"
          className="bgm-btn"
          disabled={uploading}
          onClick={() => fileInput.current?.click()}
        >
          {uploading ? "Uploading…" : "Upload file…"}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Reset first: picking the SAME file twice fires no change event
            // otherwise, so a failed upload could not be retried.
            event.target.value = "";
            if (file) void upload(file);
          }}
        />
        <span className="spacer" />
        <button type="button" className="bgm-btn" onClick={close}>
          Done
        </button>
        <button
          type="button"
          className="bgm-btn bgm-btn-primary"
          disabled={uploading || url.trim() === ""}
          onClick={saveLink}
        >
          Add link
        </button>
      </div>
      <div className="bgm-dialog-hint">
        mp3, wav, m4a, aac, ogg or flac — up to 30 MB. You can also drop files straight onto a
        mood&rsquo;s slot.
      </div>
    </div>
  );
}
